import './App.css';
import React, { useCallback, useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AppBar, Toolbar, Button, Container, Grid, Typography, Box } from '@mui/material';
import RegisterForm from './components/RegisterForm';
import UserList from './components/UserList';
import LoginForm from "./components/LoginForm";
import { getAllUsers, createUser, deleteUser } from './services/api';

/**
 * Composant principal de l'application.
 * Gère l'authentification, l'inscription et la liste des utilisateurs.
 */
function App() {
  const [users, setUsers] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setRole] = useState('');
  const [hasShownToast, setHasShownToast] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('role');
    if (token) {
      setIsAuthenticated(true);
      setRole(userRole);
      if (!hasShownToast) {
        toast.success("Utilisateur connecté");
        setHasShownToast(true); // Marque le toast comme affiché
      }
    }
  }, [isAuthenticated, hasShownToast]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setIsAuthenticated(false);
    setRole('');

    toast.info("Déconnexion réussie");

    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const users = await getAllUsers();
      setUsers(users);
    } catch (error) {
      console.error(`Erreur lors du chargement des utilisateurs : `, error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRegister = async (newUser) => {
    try {
      await createUser(newUser);
      fetchUsers();
      toast.success("Inscription réussie !");
    } catch (error) {
      console.error(`Erreur lors de l'enregistrement de l'utilisateur : `, error);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      setUsers(users.filter(user => user._id !== userId));
      toast.success("Utilisateur supprimé avec succès !");
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      toast.error("Impossible de supprimer l'utilisateur");
    }
  };

  return (
      <Router>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Integration Deploiement user
            </Typography>
            <Button color="inherit" component={Link} to="/">Accueil</Button>
            {!isAuthenticated ? (
                <Button color="inherit" component={Link} to="/login">Connexion</Button>
            ) : (
                <Button color="inherit" onClick={handleLogout}>Déconnexion</Button>
            )}
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ marginTop: 4 }}>
          <ToastContainer autoClose={3000} />

          <Routes>
            <Route path="/" element={
              <Box textAlign="center">
                <Typography variant="h3" gutterBottom>
                  Bienvenue sur notre plateforme !
                </Typography>
                <Typography variant="h6" color="textSecondary" paragraph>
                  Inscrivez-vous et gérez vos utilisateurs facilement.
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                  <Grid item xs={12} md={6}>
                    <RegisterForm onRegister={handleRegister} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <UserList users={users} onDeleteUser={handleDeleteUser} />
                  </Grid>
                </Grid>
              </Box>
            } />

            <Route path="/login" element={
              <Box textAlign="center">
                <Typography variant="h4" gutterBottom>
                  Connexion
                </Typography>
                <Typography variant="h6" color="textSecondary" paragraph>
                  Connectez-vous pour accéder à votre espace.
                </Typography>
                <Grid container justifyContent="center">
                  <Grid item xs={12} md={6}>
                    <LoginForm />
                  </Grid>
                </Grid>
              </Box>
            } />
          </Routes>
        </Container>
      </Router>
  );
}

export default App;
